import {
  ChangeDetectorRef,
  Component, EventEmitter, Input, OnDestroy, OnInit, Output
} from '@angular/core';
import {
  Subscription
} from "rxjs";
import {
  ActivatedRoute, RouterOutlet
} from "@angular/router";
import {ComponentBase} from "../component-base/component-base.component";
import {NavigationStateService} from "../../services/navigation-state.service";
import {NgClass, NgIf} from "@angular/common";


/**
 * Represents a simple container, that allows for the hierarchical display of child routes. The default
 * behaviour of the container is to hide parent contents, whenever a child route is actively displayed
 * on screen. Example use:
 * ```
 * <srs-route-container>
 *   <div>
 *     Visible until child appears.
 *   </div>
 *   <a routerLink='/x/y/z/'>Click to activate child and hide parent contents.</a>
 * </srs-route-container>
 * ```
 */
@Component({
  selector: 'route-container',
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    RouterOutlet
  ],
  templateUrl: './route-container.component.html'
})
export class RouteContainerComponent extends ComponentBase implements OnInit, OnDestroy {

  @Output() routerOutletChange = new EventEmitter<{
    activated: boolean,
    eventArgs: any
  }>()

  navDepth = -1
  navDepthSub?: Subscription
  routeUrl: string = ''
  readonly routeRgba: {
    red: number
    blue: number
    green: number
    alpha: number
  }
  readonly routePrimaryColor: string
  readonly routeHoverBackgroundColor: string
  isHover = false

  constructor(
    readonly cdr: ChangeDetectorRef,
    readonly activatedRoute: ActivatedRoute,
    readonly navService: NavigationStateService) {
    super()
    this.routeRgba = this.generateRgba(1)
    this.routePrimaryColor =
      `rgba(${this.routeRgba.red}, ${this.routeRgba.green}, ${this.routeRgba.blue}, 0.3)`
    this.routeHoverBackgroundColor =
      `rgba(${this.routeRgba.red}, ${this.routeRgba.green}, ${this.routeRgba.blue}, 0.1)`
  }

  /**
   * A flag, indicating if the primary parent content will be automatically hidden whenever a child
   * route is activated. Defaults to 'true'.
   */
  @Input() autoHideContent = true

  /** Stores references of the currently activated routing event based on the name of the router outlet */
  readonly childRouteEvents: { [id in string]: any} = {}

  /** Gets the names of all activated child router outlets. */
  // get activeChildOutlets(): string[] {
  //   return Object.keys(this.childRouteEvents)
  // }

  /** Gets an indication, if there are any active child outlets. */
    // get hasActiveChild(): boolean {
    //   return this.activeChildOutlets.length > 0
    // }

  activeChildOutlets: string[] = []
  hasActiveChild: boolean = false

  /** Event handler, invoked whenever a child router outlet is activated. */
  onChildRouteActivate(event: any, routerOutletName: string = 'primary'): void {
    this.childRouteEvents[routerOutletName] = event
    this.routerOutletChange.emit({
      activated: true,
      eventArgs: event
    })
    this.activeChildOutlets = Object.keys(this.childRouteEvents)
    this.hasActiveChild = this.activeChildOutlets.length > 0
    this.cdr.detectChanges()
  }

  /** Event handler, invoked whenever a child router outlet is deactivated. */
  onChildRouteDeactivate(event: any, routerOutletName: string = 'primary'): void {
    delete this.childRouteEvents[routerOutletName]
    this.routerOutletChange.emit({
      activated: false,
      eventArgs: event
    })
    this.activeChildOutlets = Object.keys(this.childRouteEvents)
    this.hasActiveChild = this.activeChildOutlets.length > 0
    this.cdr.detectChanges()
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.routeUrl = this.activatedRoute.snapshot.url.join('/')
    this.navDepth = this.navService.navigationDepth
    this.navDepthSub = this.navService.navigationDepthChange.subscribe({
      next: (d) => {
        this.navDepth = d
      }
    })
  }

  override ngOnDestroy(): void {
    this.navDepthSub?.unsubscribe()
    this.navDepth = -1
    super.ngOnDestroy()
  }

  generateRgba(alpha: number = 0.3) {
    return {
      red: this.generateRandomNumber(0, 250),
      green: this.generateRandomNumber(0, 250),
      blue: this.generateRandomNumber(0, 250),
      alpha: alpha
    }
  }

  generateRandomNumber(min: number, max: number) {
    min = Math.ceil(min)
    max = Math.floor(max)
    return Math.floor(Math.random() * (max - min + 1) + min)
  }
}
